import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImgIcon, Send, Wand2, Upload, RefreshCw, Calendar, Loader2, X } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/Page";
import api from "@/lib/api";

const PLACEMENT_OPTIONS = [
  { key: "facebook-feed", label: "Facebook Feed", aspect: "1:1" },
  { key: "facebook-story", label: "Facebook Story", aspect: "9:16" },
  { key: "instagram-feed", label: "Instagram Feed", aspect: "1:1" },
  { key: "instagram-story", label: "Instagram Story", aspect: "9:16" },
];

const ASPECT_HINT = (placements) => {
  const has916 = placements.some((p) => p.endsWith("story"));
  const has11 = placements.some((p) => p.endsWith("feed"));
  if (has916 && !has11) return "9:16";
  return "1:1";
};

const QuickPost = () => {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [placements, setPlacements] = useState(["facebook-feed", "instagram-feed"]);
  const [appendContact, setAppendContact] = useState(true);
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data || []);
      if (data && data.length && !brandId) setBrandId(data[0].id);
    }).catch(() => toast.error("Failed to load brands"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateImage = async () => {
    if (!prompt.trim()) { toast.error("Type a prompt first"); return; }
    setGenerating(true);
    try {
      const aspect = ASPECT_HINT(placements);
      const { data } = await api.post("/quick-post/generate-image", { prompt: prompt.trim(), aspect }, { timeout: 120000 });
      setImageUrl(data.image_url);
      toast.success("Image generated — tweak the prompt and regenerate if you want a different one");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setImageUrl(reader.result); toast.success("Image uploaded"); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const togglePlacement = (k) => {
    setPlacements((prev) => prev.includes(k) ? prev.filter((p) => p !== k) : [...prev, k]);
  };

  const publish = async () => {
    if (!brandId) { toast.error("Select a brand"); return; }
    if (!imageUrl) { toast.error("Generate or upload an image first"); return; }
    if (placements.length === 0) { toast.error("Pick at least one placement"); return; }
    if (!caption.trim() && !hashtags.trim()) {
      if (!confirm("Publish without a caption?")) return;
    }
    setPublishing(true);
    try {
      const body = {
        brand_id: brandId,
        image_url: imageUrl,
        caption: caption.trim(),
        hashtags: hashtags.trim() || null,
        placements,
        schedule_mode: scheduleMode,
        append_brand_contact: appendContact,
      };
      if (scheduleMode === "custom") body.scheduled_at = new Date(scheduledAt).toISOString();
      const { data } = await api.post("/quick-post/publish", body);
      toast.success(`Scheduled ${data.scheduled} placement${data.scheduled === 1 ? "" : "s"} · publishing ${scheduleMode === "now" ? "in ~1 min" : new Date(data.scheduled_at).toLocaleString()}`);
      // Reset for next post
      setImageUrl("");
      setPrompt("");
      setCaption("");
      setHashtags("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Quick Post" subtitle="Type a prompt → AI generates an image → you write the caption → posts to FB & IG. Same model as your ChatGPT." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT — image */}
        <div className="op-card p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ImgIcon size={16} className="text-[#7C3AED]" /> Image
          </h3>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Prompt — describe the image you want</label>
            <textarea
              className="op-input w-full min-h-[100px] text-sm"
              placeholder="e.g., A young professional student in a navy blazer holding a laptop, smiling, soft warm studio lighting, background of a modern Vancouver campus, photorealistic"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="qp-prompt"
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button onClick={generateImage} disabled={generating || !prompt.trim()} className="op-btn-primary text-sm" data-testid="qp-generate">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                {generating ? "Generating…" : (imageUrl ? "Regenerate" : "Generate image")}
              </button>
              <label className="op-btn-secondary text-sm cursor-pointer">
                <Upload size={14} /> Upload your own
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} className="hidden" data-testid="qp-upload" />
              </label>
              {imageUrl && (
                <button onClick={() => setImageUrl("")} className="op-btn-secondary text-xs text-rose-600" data-testid="qp-clear-image">
                  <X size={12} /> Clear
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Aspect: {ASPECT_HINT(placements)} (auto from selected placements). Don't like the result? Edit the prompt and click Regenerate.</p>
          </div>

          <div className="rounded-xl bg-gray-50 border border-gray-200 aspect-square flex items-center justify-center overflow-hidden" data-testid="qp-preview">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="w-full h-full object-cover" />
            ) : generating ? (
              <div className="flex flex-col items-center text-gray-400 gap-2">
                <Loader2 className="animate-spin" size={32} />
                <p className="text-xs">Generating with GPT Image 1…</p>
                <p className="text-[10px]">~20-40 seconds</p>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No image yet — generate or upload</p>
            )}
          </div>
        </div>

        {/* RIGHT — caption + publish */}
        <div className="op-card p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Send size={16} className="text-[#7C3AED]" /> Caption & Publish
          </h3>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Brand</label>
            <select className="op-input w-full text-sm" value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="qp-brand">
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Caption</label>
            <textarea
              className="op-input w-full min-h-[140px] text-sm"
              placeholder="Write the post copy. Or generate ideas via the Approval Queue if you want AI to draft it."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="qp-caption"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Hashtags (optional)</label>
            <input className="op-input w-full text-sm" placeholder="#StudyInBC #HCC #DiplomaPrograms"
                   value={hashtags} onChange={(e) => setHashtags(e.target.value)} data-testid="qp-hashtags" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={appendContact} onChange={(e) => setAppendContact(e.target.checked)} data-testid="qp-append-contact" />
            <span className="text-xs text-gray-700">Auto-append brand phone/email/website to the caption</span>
          </label>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Post to</label>
            <div className="grid grid-cols-2 gap-2" data-testid="qp-placements">
              {PLACEMENT_OPTIONS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-colors text-sm"
                       style={{borderColor: placements.includes(p.key) ? "#7C3AED" : "#E5E7EB", background: placements.includes(p.key) ? "#FAF5FF" : "#fff"}}>
                  <input type="checkbox" checked={placements.includes(p.key)} onChange={() => togglePlacement(p.key)} />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">When</label>
            <div className="flex gap-2 flex-wrap mb-2">
              <button onClick={() => setScheduleMode("now")}
                      className={`px-3 py-2 rounded-lg text-sm border-2 ${scheduleMode === "now" ? "border-[#7C3AED] bg-purple-50 text-[#7C3AED] font-medium" : "border-gray-200"}`}
                      data-testid="qp-mode-now">
                Post now
              </button>
              <button onClick={() => setScheduleMode("custom")}
                      className={`px-3 py-2 rounded-lg text-sm border-2 inline-flex items-center gap-1 ${scheduleMode === "custom" ? "border-[#7C3AED] bg-purple-50 text-[#7C3AED] font-medium" : "border-gray-200"}`}
                      data-testid="qp-mode-schedule">
                <Calendar size={12} /> Schedule
              </button>
            </div>
            {scheduleMode === "custom" && (
              <input type="datetime-local" className="op-input text-sm w-full" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="qp-scheduled-at" />
            )}
          </div>

          <button onClick={publish} disabled={publishing || !imageUrl || !brandId} className="op-btn-primary w-full text-sm" data-testid="qp-publish">
            {publishing ? <><Loader2 className="animate-spin" size={14} /> Publishing…</> : <><Send size={14} /> {scheduleMode === "now" ? "Post now" : "Schedule post"}</>}
          </button>
          <p className="text-[10px] text-gray-400">Auto-publishes via the background scheduler. Watch the Approval Queue → Scheduled tab to track delivery.</p>
        </div>
      </div>
    </PageContainer>
  );
};

export default QuickPost;
